import React, { useState, useEffect, useCallback } from "react";
import {
  getSettings,
  updateSettings,
  getRaceTopSetting,
  updateRaceTopSetting,
  getLotteryLuckySetting,
  updateLotteryLuckySetting,
} from "../../api/settingApi";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./style.css";

const MySwal = withReactContent(Swal);

// ====== HÀM XỬ LÝ MÚI GIỜ GMT+8 ======
// Hiển thị: timestamp (UTC) => input GMT+8
function formatTimestampToInputGMT8(timestamp) {
  if (!timestamp) return "";
  const date = new Date(Number(timestamp));
  let year = date.getUTCFullYear();
  let month = date.getUTCMonth() + 1;
  let day = date.getUTCDate();
  let hours = date.getUTCHours() + 8;
  let minutes = date.getUTCMinutes();
  // Chuẩn hóa giờ, ngày, tháng, năm nếu vượt quá 23h
  if (hours >= 24) {
    hours -= 24;
    day++;
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) {
      day = 1;
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }
  }
  const pad = (n) => n.toString().padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}`;
}
// Lưu: input GMT+8 => timestamp GMT+8
function inputGMT8ToTimestamp(inputValue) {
  if (!inputValue) return 0;
  const [date, time] = inputValue.split("T");
  const [year, month, day] = date.split("-");
  const [hour, minute] = time.split(":");
  // Tạo Date ở GMT+8
  const dateInGMT8 = new Date(Date.UTC(year, month - 1, day, hour, minute));
  // Trừ đi 8 tiếng để ra timestamp GMT+8
  return dateInGMT8.getTime() - 8 * 60 * 60 * 1000;
}

const SettingPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  // State cho cấu hình đua top
  const [raceTopConfig, setRaceTopConfig] = useState({
    status: false,
    start_timestamp: "",
    end_timestamp: "",
  });

  // State cho cấu hình nhận mã dự thưởng
  const [lotteryLuckyConfig, setLotteryLuckyConfig] = useState({
    site: process.env.REACT_APP_SITE || "78win",
    status: false,
    start_timestamp: "",
    end_timestamp: "",
    conditions: [],
  });

  const fetchSettings = useCallback(async () => {
    try {
      const [attendanceRes, raceTopRes] = await Promise.all([
        getSettings(),
        getRaceTopSetting(),
      ]);
      const data = attendanceRes.data;
      // Convert timestamps to yyyy-MM-ddTHH:mm format for datetime-local input (always show as GMT+8)
      if (data.start_timestamp) {
        data.start_timestamp = formatTimestampToInputGMT8(data.start_timestamp);
      }
      if (data.end_timestamp) {
        data.end_timestamp = formatTimestampToInputGMT8(data.end_timestamp);
      }
      setSettings(data);
      // Xử lý dữ liệu đua top
      const raceTopData = raceTopRes.data;
      setRaceTopConfig({
        status: !!raceTopData.status,
        start_timestamp: raceTopData.start_timestamp
          ? formatTimestampToInputGMT8(raceTopData.start_timestamp)
          : "",
        end_timestamp: raceTopData.end_timestamp
          ? formatTimestampToInputGMT8(raceTopData.end_timestamp)
          : "",
      });
    } catch (err) {
      console.error("Fetch Settings error:", err);
      if (err.response?.status !== 403) {
        MySwal.fire(
          "Lỗi!",
          "Không thể tải dữ liệu cài đặt. " +
            (err.response?.data?.message || err.message),
          "error"
        );
      }
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  }, []);

  const fetchLotteryLuckySetting = useCallback(async () => {
    try {
      const res = await getLotteryLuckySetting();
      const data = res.data;
      setLotteryLuckyConfig({
        ...data,
        start_timestamp: data.start_timestamp
          ? formatTimestampToInputGMT8(data.start_timestamp)
          : "",
        end_timestamp: data.end_timestamp
          ? formatTimestampToInputGMT8(data.end_timestamp)
          : "",
        conditions: data.conditions || [],
      });
    } catch (err) {
      // Không cần báo lỗi nếu chưa có config
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchLotteryLuckySetting();
  }, [fetchSettings, fetchLotteryLuckySetting]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStreakChange = (index, field, value) => {
    const newStreaks = [...settings.streak_attendance_point];
    newStreaks[index][field] = Number(value);
    setSettings((prev) => ({ ...prev, streak_attendance_point: newStreaks }));
  };

  const addStreak = () => {
    setSettings((prev) => ({
      ...prev,
      streak_attendance_point: [
        ...prev.streak_attendance_point,
        { day: 0, point: 0 },
      ],
    }));
  };

  const removeStreak = (index) => {
    const newStreaks = settings.streak_attendance_point.filter(
      (_, i) => i !== index
    );
    setSettings((prev) => ({ ...prev, streak_attendance_point: newStreaks }));
  };

  const handleDangerGroupChange = (index, value) => {
    const newGroups = [...settings.group_danger];
    newGroups[index] = value;
    setSettings((prev) => ({ ...prev, group_danger: newGroups }));
  };

  const addDangerGroup = () => {
    setSettings((prev) => ({
      ...prev,
      group_danger: [...(prev.group_danger || []), ""],
    }));
  };

  const removeDangerGroup = (index) => {
    const newGroups = settings.group_danger.filter((_, i) => i !== index);
    setSettings((prev) => ({ ...prev, group_danger: newGroups }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Convert local datetime (GMT+8) to timestamp GMT+8
    const dataToSubmit = {
      ...settings,
      start_timestamp: inputGMT8ToTimestamp(settings.start_timestamp),
      bonus_point_day: Number(settings.bonus_point_day || 0),
      level_vip: Number(settings.level_vip || 0),
      total_deposit: Number(settings.total_deposit || 0),
      total_valid_bet: Number(settings.total_valid_bet || 0),
    };
    // Remove id from the object before sending
    const { _id, ...rest } = dataToSubmit;

    try {
      await updateSettings(rest);
      MySwal.fire("Thành công!", "Cài đặt đã được cập nhật.", "success");
    } catch (err) {
      console.error("Update settings error:", err);
      if (err.response?.status !== 403) {
        MySwal.fire(
          "Lỗi!",
          "Cập nhật cài đặt thất bại. " +
            (err.response?.data?.message || err.message),
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Hàm xử lý thay đổi input đua top
  const handleRaceTopChange = (e) => {
    const { name, type, checked, value } = e.target;
    setRaceTopConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Hàm submit đua top
  const handleRaceTopSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        site: process.env.REACT_APP_SITE || "78win",
        status: raceTopConfig.status,
        start_timestamp: inputGMT8ToTimestamp(raceTopConfig.start_timestamp),
        end_timestamp: inputGMT8ToTimestamp(raceTopConfig.end_timestamp),
      };
      await updateRaceTopSetting(payload);
      MySwal.fire(
        "Thành công!",
        "Cấu hình đua top đã được cập nhật.",
        "success"
      );
    } catch (err) {
      MySwal.fire("Lỗi!", "Cập nhật cấu hình đua top thất bại.", "error");
    }
  };

  const handleLotteryLuckyChange = (e) => {
    const { name, type, checked, value } = e.target;
    setLotteryLuckyConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleConditionChange = (idx, field, value) => {
    const newConditions = [...lotteryLuckyConfig.conditions];
    newConditions[idx][field] =
      field === "type_code" ? value.split(",") : value;
    setLotteryLuckyConfig((prev) => ({ ...prev, conditions: newConditions }));
  };

  const addCondition = () => {
    setLotteryLuckyConfig((prev) => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          code: "",
          name: "",
          deposit: 0,
          valid_bet: 0,
          code_count: 0,
          type_code: [],
        },
      ],
    }));
  };

  const removeCondition = (idx) => {
    setLotteryLuckyConfig((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== idx),
    }));
  };

  const handleLotteryLuckySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...lotteryLuckyConfig,
        start_timestamp: inputGMT8ToTimestamp(
          lotteryLuckyConfig.start_timestamp
        ),
        end_timestamp: inputGMT8ToTimestamp(lotteryLuckyConfig.end_timestamp),
        conditions: lotteryLuckyConfig.conditions.map((c) => ({
          ...c,
          deposit: Number(c.deposit),
          valid_bet: Number(c.valid_bet),
          code_count: Number(c.code_count),
          type_code: Array.isArray(c.type_code)
            ? c.type_code
            : c.type_code
            ? c.type_code.split(",")
            : [],
        })),
      };
      await updateLotteryLuckySetting(payload);
      MySwal.fire(
        "Thành công!",
        "Cấu hình nhận mã dự thưởng đã được cập nhật.",
        "success"
      );
    } catch (err) {
      MySwal.fire(
        "Lỗi!",
        "Cập nhật cấu hình nhận mã dự thưởng thất bại.",
        "error"
      );
    }
  };

  if (initialLoading) {
    return <div className="loading-container">Đang tải cài đặt...</div>;
  }

  if (!settings) {
    return (
      <div className="loading-container">
        Không tìm thấy dữ liệu cài đặt cho site này.
      </div>
    );
  }

  return (
    <div className="settings-page">
      <form
        id="race-top-settings-form"
        onSubmit={handleRaceTopSubmit}
        className="settings-form"
        style={{ marginTop: 0, marginBottom: 32 }}
      >
        <div className="users-header">
          <div className="header-content">
            <h3>Cấu hình đua top</h3>
          </div>
        </div>
        <div className="form-top-bar">
          <div className="form-group status-group">
            <label>Trạng thái</label>
            <label className="switch">
              <input
                type="checkbox"
                name="status"
                checked={raceTopConfig.status}
                onChange={handleRaceTopChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
          <button type="submit" className="save-button">
            Lưu thay đổi
          </button>
        </div>
        <div className="form-grid">
          <div className="form-column">
            <div className="form-group">
              <label>Thời gian bắt đầu</label>
              <input
                type="datetime-local"
                name="start_timestamp"
                value={raceTopConfig.start_timestamp}
                onChange={handleRaceTopChange}
              />
            </div>
            <div className="form-group">
              <label>Thời gian kết thúc</label>
              <input
                type="datetime-local"
                name="end_timestamp"
                value={raceTopConfig.end_timestamp}
                onChange={handleRaceTopChange}
              />
            </div>
          </div>
        </div>
      </form>

      <form
        id="settings-form"
        onSubmit={handleSubmit}
        className="settings-form"
      >
        <div className="header-content">
          <h3>Cấu hình điểm danh</h3>
        </div>
        <div className="form-top-bar">
          <div className="form-group status-group">
            <label>Trạng thái</label>
            <label className="switch">
              <input
                type="checkbox"
                name="status"
                checked={settings.status || false}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
          <button type="submit" className="save-button" disabled={loading}>
            {loading ? "Đang lưu..." : "Lưu thay đổi"}
          </button>
        </div>

        <div className="form-grid">
          {/* Left Column */}
          <div className="form-column">
            <div className="form-group">
              <label>Thời gian bắt đầu</label>
              <input
                type="datetime-local"
                name="start_timestamp"
                value={settings.start_timestamp || ""}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Điểm thưởng mỗi ngày</label>
              <input
                type="number"
                name="bonus_point_day"
                value={settings.bonus_point_day || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Điểm thưởng chuỗi điểm danh</label>
              <div className="dynamic-list">
                {settings.streak_attendance_point?.map((streak, index) => (
                  <div key={index} className="dynamic-item streak-item">
                    <input
                      type="number"
                      placeholder="Ngày"
                      value={streak.day}
                      onChange={(e) =>
                        handleStreakChange(index, "day", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      placeholder="Điểm"
                      value={streak.point}
                      onChange={(e) =>
                        handleStreakChange(index, "point", e.target.value)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeStreak(index)}
                      className="remove-btn"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addStreak} className="add-btn">
                  Thêm mốc
                </button>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="form-column">
            <div className="form-group">
              <label>VIP tối thiểu</label>
              <input
                type="number"
                name="level_vip"
                value={settings.level_vip || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Tổng nạp hợp lệ</label>
              <input
                type="number"
                name="total_deposit"
                value={settings.total_deposit || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Tổng cược hợp lệ</label>
              <input
                type="number"
                name="total_valid_bet"
                value={settings.total_valid_bet || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Nhóm lạm dụng</label>
              <div className="dynamic-list">
                {settings.group_danger?.map((group, index) => (
                  <div key={index} className="dynamic-item">
                    <input
                      type="text"
                      placeholder="Tên nhóm"
                      value={group}
                      onChange={(e) =>
                        handleDangerGroupChange(index, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeDangerGroup(index)}
                      className="remove-btn"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addDangerGroup}
                  className="add-btn"
                >
                  Thêm nhóm
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div style={{ height: 32 }} />
      <form
        id="lottery-lucky-settings-form"
        onSubmit={handleLotteryLuckySubmit}
        className="settings-form"
        style={{ marginTop: 0, marginBottom: 32 }}
      >
        <div className="users-header">
          <div className="header-content">
            <h3>Cấu hình nhận mã dự thưởng</h3>
          </div>
        </div>
        <div className="form-top-bar">
          <div className="form-group status-group">
            <label>Trạng thái</label>
            <label className="switch">
              <input
                type="checkbox"
                name="status"
                checked={lotteryLuckyConfig.status}
                onChange={handleLotteryLuckyChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
          <button type="submit" className="save-button">
            Lưu thay đổi
          </button>
        </div>
        <div
          className="form-row"
          style={{ display: "flex", gap: 20, marginBottom: 24 }}
        >
          <div className="form-group" style={{ flex: 1 }}>
            <label>Thời gian bắt đầu</label>
            <input
              type="datetime-local"
              name="start_timestamp"
              value={lotteryLuckyConfig.start_timestamp}
              onChange={handleLotteryLuckyChange}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Thời gian kết thúc</label>
            <input
              type="datetime-local"
              name="end_timestamp"
              value={lotteryLuckyConfig.end_timestamp}
              onChange={handleLotteryLuckyChange}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Điều kiện nhận mã</label>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 4,
              fontSize: 13,
              color: "#666",
              fontWeight: 500,
            }}
          >
            <div style={{ minWidth: 100, flex: 1 }}>Mã điều kiện</div>
            <div style={{ minWidth: 120, flex: 2 }}>Tên điều kiện</div>
            <div style={{ minWidth: 100, flex: 1 }}>Nạp tối thiểu</div>
            <div style={{ minWidth: 120, flex: 1 }}>Cược hợp lệ tối thiểu</div>
            <div style={{ minWidth: 100, flex: 1 }}>Số lượng mã</div>
            <div style={{ minWidth: 120, flex: 2 }}>
              Loại mã quay thưởng (phẩy ,)
            </div>
            <div style={{ width: 32 }}></div>
          </div>
          <div className="dynamic-list" style={{ width: "100%" }}>
            {lotteryLuckyConfig.conditions.map((cond, idx) => (
              <div key={idx} className="dynamic-item" style={{ gap: 12 }}>
                <input
                  type="text"
                  placeholder="Mã điều kiện"
                  value={cond.code}
                  onChange={(e) =>
                    handleConditionChange(idx, "code", e.target.value)
                  }
                  style={{ minWidth: 100, flex: 1 }}
                />
                <input
                  type="text"
                  placeholder="Tên điều kiện"
                  value={cond.name}
                  onChange={(e) =>
                    handleConditionChange(idx, "name", e.target.value)
                  }
                  style={{ minWidth: 120, flex: 2 }}
                />
                <input
                  type="number"
                  placeholder="Nạp tối thiểu"
                  value={cond.deposit}
                  onChange={(e) =>
                    handleConditionChange(idx, "deposit", e.target.value)
                  }
                  style={{ minWidth: 100, flex: 1 }}
                />
                <input
                  type="number"
                  placeholder="Cược hợp lệ tối thiểu"
                  value={cond.valid_bet}
                  onChange={(e) =>
                    handleConditionChange(idx, "valid_bet", e.target.value)
                  }
                  style={{ minWidth: 120, flex: 1 }}
                />
                <input
                  type="number"
                  placeholder="Số lượng mã"
                  value={cond.code_count}
                  onChange={(e) =>
                    handleConditionChange(idx, "code_count", e.target.value)
                  }
                  style={{ minWidth: 100, flex: 1 }}
                />
                <input
                  type="text"
                  placeholder="Loại mã quay thưởng (phẩy ,)"
                  value={
                    Array.isArray(cond.type_code)
                      ? cond.type_code.join(",")
                      : cond.type_code
                  }
                  onChange={(e) =>
                    handleConditionChange(idx, "type_code", e.target.value)
                  }
                  style={{ minWidth: 120, flex: 2 }}
                />
                <button
                  type="button"
                  onClick={() => removeCondition(idx)}
                  className="remove-btn"
                >
                  &times;
                </button>
              </div>
            ))}
            <button type="button" onClick={addCondition} className="add-btn">
              Thêm điều kiện
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingPage;
